// Swedish bank account utilities built on the `kontonummer` registry: bank lookup and
// transport normalization (ADA-4344, moved out of validation.ts in the PR #912 review —
// these derive values, they don't validate). The validation predicates in validation.ts
// build on these; imports flow validation → here, never the other way.

import Kontonummer from 'kontonummer'

/**
 * The bank a clearing number belongs to, or undefined when no bank claims it.
 *
 * This is what replaces the design's "Välj bank" dropdown: in Sweden the bank follows from the
 * clearing number, so asking for both invites a contradiction. The name is shown back to the
 * debtor as confirmation that they typed the number they meant.
 */
export function bankNameForClearing(clearingNumber: string): string | undefined {
  const clearing = clearingNumber.replace(/\D/g, '')
  // Shape gate mirrors validation.ts's isValidClearingNumber (kept inline so the import
  // direction stays one-way).
  if (!/^[0-9]{4,5}$/.test(clearing)) return undefined
  if (clearing.length === 5 && (!clearing.startsWith('8') || !passesLuhn(clearing))) {
    // The package reads only the first four digits, so "12345" would render as Danske Bank
    // while the alert calls the clearing number invalid — no name for a rejected clearing.
    return undefined
  }
  return bankInfoFor(clearing)?.bankName
}

/** The registry entry claiming a clearing number, or undefined when no bank does. Verified:
 * the package THROWS for a clearing number outside every known range (9999), it does not
 * return undefined — so the catch is the "no such bank" path, not a safety net. */
function bankInfoFor(clearingNumber: string): ReturnType<typeof Kontonummer.getSortingCodeInfo> | undefined {
  try {
    return Kontonummer.getSortingCodeInfo(Number(clearingNumber))
  } catch {
    return undefined
  }
}

/**
 * Rules a bank enforces beyond the registry, keyed on the registry entry's clearing range like the
 * api's `_BANK_RULES`: the account length the bank pays to, the fixed width it reads the account
 * in, a prefix it does not read, and the text for an account over its cap. Swedbank 8xxx pays to
 * ten digits (the registry's eleven exists for a fifth clearing digit that slipped into the
 * account) and reads a shorter account zero-padded to ten — older accounts were issued without
 * their leading zeros, and the api requires the padded form. Nordea personkonto (3300/3782) is the
 * holder's ten-digit personal identity number: entered with its century digits (18, 19, 20) it has
 * twelve, of which the bank reads the last ten; any other twelve-digit entry is over the cap.
 */
export interface PrefixStrip {
  /** The account length the prefix is read at — nothing is dropped from any other length. */
  fromLength: number
  count: number
  /** The leading digits that identify the prefix; anything else is left for the cap to refuse. */
  prefixes: readonly string[]
}

export interface BankRule {
  /** The registry entry's clearing range the rule is keyed on. */
  range: readonly [number, number]
  accountMaxLength: number
  /** The bank reads the account at exactly accountMaxLength digits; a shorter account is zero-padded to it. */
  accountFixedWidth: boolean
  accountPrefixStrip?: PrefixStrip
  /** The locale key behind `invalid_account_length` — the bank's rule names its own text. */
  lengthIssueKey: string
}

const BANK_RULES: readonly BankRule[] = [
  {
    range: [8000, 8999],
    accountMaxLength: 10,
    accountFixedWidth: true,
    lengthIssueKey: 'common.swedbank_account_too_long',
  },
  // Nordea personkonto: the registry entry carries both 3300 and 3782, and bankRuleFor matches the entry, so one row covers both.
  {
    range: [3300, 3300],
    accountMaxLength: 10,
    accountFixedWidth: true,
    accountPrefixStrip: { fromLength: 12, count: 2, prefixes: ['18', '19', '20'] },
    lengthIssueKey: 'common.nordea_personkonto_too_long',
  },
]

/** The bank rule for a registry entry, or undefined when the registry alone decides. */
function bankRuleFor(bank: ReturnType<typeof Kontonummer.getSortingCodeInfo>): BankRule | undefined {
  return BANK_RULES.find((rule) => bank.ranges.some(([low, high]) => low === rule.range[0] && high === rule.range[1]))
}

/** The bank rule for a clearing number — the one definition validation.ts and the fields read the cap and its text from. */
export function bankRuleForClearing(clearingNumber: string): BankRule | undefined {
  const bank = bankInfoFor(clearingNumber.replace(/\D/g, ''))
  return bank == null ? undefined : bankRuleFor(bank)
}

/**
 * Clearing + account number reduced to what is actually sent and stored (ADA-4344 decision #5):
 * digits only, leading zeros beyond the bank's maximum account length dropped, a prefix the bank
 * does not read dropped (Nordea personkonto: the century digits), type 1 accounts zero-padded to
 * the canonical seven digits and fixed-width accounts (Swedbank 8xxx, Nordea personkonto) to ten —
 * the same normalization the api applies, so the value the debtor confirmed is the value that is paid.
 * An unknown bank normalizes to digits only; the registry check then rejects it separately.
 */
export function normalizeBankAccount(
  clearingNumber: string,
  accountNumber: string
): { clearingNumber: string; accountNumber: string } {
  const clearing = clearingNumber.replace(/\D/g, '')
  let account = accountNumber.replace(/\D/g, '')
  const bank = bankInfoFor(clearing)
  if (bank == null || account === '') return { clearingNumber: clearing, accountNumber: account }
  const rule = bankRuleFor(bank)
  // Every type 2 entry carries accountMaxLength (verified against the registry); the
  // fallback only satisfies the type and means "strip nothing".
  const maxLength = bank.type === 1 ? 7 : (rule?.accountMaxLength ?? bank.accountMaxLength ?? account.length)
  while (account.length > maxLength && account.startsWith('0')) account = account.slice(1)
  // Padding first, prefix second — "00" + ten digits is padding, "19" + ten digits is a century.
  const strip = rule?.accountPrefixStrip
  if (account.length === strip?.fromLength && strip.prefixes.includes(account.slice(0, strip.count))) {
    account = account.slice(strip.count)
  }
  // Type 1 accounts are stored padded to their full seven positions (the specification's
  // canonical form): unpadded they would violate the api schema's ^[0-9]{5,15}$ and
  // concatenate into a payment-file form the bank rejects.
  if (bank.type === 1) account = account.padStart(7, '0')
  // A bank that reads a fixed width gets the account padded to it, but only once the account passes
  // the shape floor (validation.ts's rule, inline for the one-way import): a shorter entry stays as
  // typed so the "5–15 digits" rule can refuse it instead of padding it into a shape-valid value.
  if (
    rule != null &&
    rule.accountFixedWidth &&
    /^[0-9]{5,15}$/.test(account) &&
    account.length < rule.accountMaxLength
  ) {
    account = account.padStart(rule.accountMaxLength, '0')
  }
  return { clearingNumber: clearing, accountNumber: account }
}

/**
 * A giro number reduced to what is actually sent and stored (ADA-4350 decision #5): digits only,
 * leading zeros dropped.
 *
 * Luhn is invariant under left zero-padding and the transport format (BGMAX) re-pads to its own
 * width on the way out, so the significant digits ARE the number. Stripping them gives one
 * canonical stored form, so the same giro cannot end up stored in two spellings — the api applies
 * exactly this normalization (SwedishGiroHelper), and the length is judged on the result.
 *
 * Both instruments (plusgiro joined in ADA-4349). Note that the strip can move a number across a
 * length boundary — '007' is a one-digit plusgiro, below the 2–8 floor — so every caller must
 * length-check the RESULT, not the raw input.
 */
export function normalizeGiro(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+/, '')
}

/**
 * Input masks per instrument, mirrored by hand from ada-api's `Config.UI.MaskFormats` (the `#mask:`
 * directive on payout_details.bankgiro/.plusgiro): this app has no swagger reader, so keep them in sync.
 */
export const BANKGIRO_MASKS: readonly string[] = ['###-####', '####-####']
export const PLUSGIRO_MASKS: readonly string[] = ['#-#', '##-#', '###-#', '####-#', '#####-#', '######-#', '#######-#']

/**
 * Luhn for a giro number — the mod 10 check plus the guard the bare sum cannot express.
 *
 * `passesLuhn` sums digits, so '' and '0000000' both reduce to `0 % 10 === 0` and would read as
 * valid; the giro shape regexes accept the all-zero form, so nothing else stops it. The api
 * guards the same trap on its side (ADA-4350 decision #2), so both ends reject it alike.
 */
export function passesGiroLuhn(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  if (!/[1-9]/.test(digits)) return false
  return passesLuhn(digits)
}

/**
 * Luhn (mod 10) check, the algorithm behind bankgiro/plusgiro check digits and the fifth digit
 * of Swedbank's five-digit clearing numbers.
 *
 * Hand-rolled because `kontonummer` covers bank accounts only, and this is the whole of the rule.
 * Callers on giro numbers must go through `passesGiroLuhn` — see its note on all-zero values.
 */
export function passesLuhn(value: string): boolean {
  let sum = 0
  let double = false
  for (let index = value.length - 1; index >= 0; index -= 1) {
    let digit = Number(value[index])
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    double = !double
  }
  return sum % 10 === 0
}
